import React, { useState } from 'react';
import { 
  Book, 
  Video, 
  MessageCircle, 
  Phone, 
  Mail, 
  Calendar, 
  Download, 
  Search,
  ChevronDown,
  ChevronRight,
  Play,
  FileText,
  Code,
  Settings,
  Users,
  HelpCircle,
  ExternalLink,
  Headphones,
  BookOpen,
  Zap,
  CheckCircle
} from 'lucide-react';

const SupportPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('getting-started');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    package: 'not-selected'
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Here you would typically send the form data to your backend
    console.log('Form submitted:', formData);
    setFormSubmitted(true);
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        package: 'not-selected'
      });
      setFormSubmitted(false);
    }, 3000);
  };

  const supportCategories = [
    { id: 'getting-started', name: 'Getting Started', icon: Play },
    { id: 'customization', name: 'Customization', icon: Settings },
    { id: 'development', name: 'Development', icon: Code },
    { id: 'deployment', name: 'Deployment', icon: Download },
    { id: 'integrations', name: 'Integrations', icon: Settings },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: HelpCircle }
  ];

  const documentationSections: Record<string, Array<{title: string, description: string, duration: string, type: string}>> = {
    'getting-started': [
      { title: 'Quick Start Guide', description: 'Get your platform running in 15 minutes', duration: '15 min read', type: 'guide' },
      { title: 'Platform Overview', description: 'Understanding all features and capabilities', duration: '20 min read', type: 'guide' },
      { title: 'First Steps Checklist', description: 'Essential tasks after getting your platform', duration: '10 min read', type: 'checklist' },
      { title: 'Video: Platform Tour', description: 'Complete walkthrough of the entire system', duration: '12 min watch', type: 'video' }
    ],
    'customization': [
      { title: 'Branding & White-Labeling', description: 'How to add your branding and customize appearance', duration: '25 min read', type: 'guide' },
      { title: 'Industry-Specific Customization', description: 'Adapting features for different industries', duration: '30 min read', type: 'guide' },
      { title: 'Custom Fields & Forms', description: 'Adding custom data fields and forms', duration: '15 min read', type: 'guide' },
      { title: 'UI/UX Customization', description: 'Modifying layouts and user interface', duration: '35 min read', type: 'guide' }
    ],
    'development': [
      { title: 'Technical Architecture', description: 'Understanding the platform architecture', duration: '45 min read', type: 'technical' },
      { title: 'API Documentation', description: 'Complete API reference and examples', duration: '60 min read', type: 'api' },
      { title: 'Database Schema', description: 'Database structure and relationships', duration: '30 min read', type: 'database' },
      { title: 'Development Environment Setup', description: 'Setting up local development environment', duration: '20 min read', type: 'setup' }
    ],
    'deployment': [
      { title: 'Server Requirements', description: 'Minimum and recommended server specifications', duration: '10 min read', type: 'requirements' },
      { title: 'Deployment Guide', description: 'Step-by-step deployment instructions', duration: '40 min read', type: 'guide' },
      { title: 'SSL & Security Setup', description: 'Configuring SSL certificates and security', duration: '25 min read', type: 'security' },
      { title: 'Performance Optimization', description: 'Optimizing your platform for best performance', duration: '30 min read', type: 'optimization' }
    ],
    'integrations': [
      { title: 'Payment Gateway Integration', description: 'Setting up Stripe, PayPal, and other gateways', duration: '35 min read', type: 'integration' },
      { title: 'Calendar Integrations', description: 'Integrating with Google Calendar, Outlook, etc.', duration: '25 min read', type: 'integration' }
    ],
    'troubleshooting': [
      { title: 'Common Issues & Solutions', description: 'Most frequent problems and their solutions', duration: '20 min read', type: 'troubleshooting' },
      { title: 'Error Codes Reference', description: 'Complete list of error codes and meanings', duration: '15 min read', type: 'reference' },
      { title: 'Performance Issues', description: 'Diagnosing and fixing performance problems', duration: '30 min read', type: 'troubleshooting' },
      { title: 'Backup & Recovery', description: 'Data backup and disaster recovery procedures', duration: '25 min read', type: 'guide' }
    ]
  };

  const videoTutorials = [
    {
      title: 'Complete Platform Setup',
      description: 'Full walkthrough from unboxing to launch',
      duration: '45:30',
      views: '12.5K',
      category: 'Getting Started'
    },
    {
      title: 'White-Label Customization',
      description: 'How to brand and customize for your business',
      duration: '28:15',
      views: '8.2K',
      category: 'Customization'
    },
    {
      title: 'Advanced Features Demo',
      description: 'Deep dive into advanced platform capabilities',
      duration: '52:20',
      views: '6.8K',
      category: 'Advanced'
    },
    {
      title: 'Integration Masterclass',
      description: 'Setting up all major integrations',
      duration: '38:45',
      views: '5.4K',
      category: 'Integrations'
    },
    {
      title: 'Deployment & Go-Live',
      description: 'Taking your platform live step-by-step',
      duration: '33:10',
      views: '9.1K',
      category: 'Deployment'
    },
    {
      title: 'Troubleshooting Common Issues',
      description: 'Solutions to the most common problems',
      duration: '22:35',
      views: '4.2K',
      category: 'Support'
    }
  ];

  const faqs = [
    {
      question: 'How long does it take to set up the platform?',
      answer: 'With our Done-For-You package, we prioritize your customization with fast-track service. With the DIY package, you can start customizing immediately with our comprehensive setup guide.'
    },
    {
      question: 'Do I need technical knowledge to use this platform?',
      answer: 'For the Done-For-You package, no technical knowledge is required - we handle everything. For the DIY package, basic web hosting knowledge is helpful, but our comprehensive documentation guides you through every step.'
    },
    {
      question: 'What kind of support do I get?',
      answer: 'DIY package includes 30 days of email support. Done-For-You package includes 90 days of premium support plus a training session. All packages include access to our documentation and video tutorials.'
    },
    {
      question: 'Can I customize the platform after purchase?',
      answer: 'Absolutely! You have complete access to the source code and can make unlimited customizations. We provide detailed customization guides and examples.'
    },
    {
      question: 'What if I need help with integrations?',
      answer: 'Our documentation includes step-by-step integration guides for all major services. Premium support customers get hands-on help with integrations.'
    },
    {
      question: 'How do I get updates and new features?',
      answer: 'Platform updates and new features are provided free for the first year. After that, updates are available for a small annual fee.'
    },
    {
      question: 'Can you help with custom development?',
      answer: 'Yes, we offer custom development services for specific features or integrations not included in the standard platform. Contact us for a quote.'
    }
  ];

  const supportChannels = [
    {
      channel: 'Documentation',
      description: 'Comprehensive guides and tutorials',
      icon: Book,
      response: 'Instant access',
      best_for: 'Step-by-step instructions'
    },
    {
      channel: 'Email Support',
      description: 'Technical questions and issues',
      icon: Mail,
      response: 'Within 24 hours',
      best_for: 'Detailed technical help'
    },
    {
      channel: 'Live Chat',
      description: 'Quick questions and guidance',
      icon: MessageCircle,
      response: 'Real-time',
      best_for: 'Immediate assistance'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-innovation text-white relative overflow-hidden">
      {/* Global Animated Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-transparent"></div>
      <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-[500px] h-[500px] bg-cyan-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '2s'}}></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-purple-500/5 rounded-full blur-2xl animate-pulse" style={{animationDelay: '4s'}}></div>
      <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-blue-500/8 rounded-full blur-3xl animate-pulse" style={{animationDelay: '6s'}}></div>
      
      {/* Hero Section */}
      <section className="py-20 relative z-10 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white via-blue-100 to-cyan-300 bg-clip-text text-transparent">
            Get Help When You Need It,<br />Succeed When You Want It
          </h1>
          <p className="text-xl md:text-2xl text-slate-300 max-w-4xl mx-auto mb-8 leading-relaxed">
            You're not just buying a platform - you're <span className="text-cyan-300 font-semibold">joining a community of successful entrepreneurs</span>. 
            Get expert support, comprehensive training, and <span className="text-yellow-300 font-semibold">proven strategies</span> that turn platforms into profit.
          </p>
          <div className="mb-8 flex flex-col sm:flex-row gap-4 justify-center">
            <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-xl border border-blue-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-blue-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <Headphones className="h-5 w-5 text-blue-400" />
                24/7 Expert Support
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-500/20 to-emerald-400/20 backdrop-blur-xl border border-green-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-green-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <BookOpen className="h-5 w-5 text-green-400" />
                Complete Training Library
              </div>
            </div>
            <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 backdrop-blur-xl border border-purple-300/30 text-white px-8 py-4 rounded-2xl text-lg font-bold shadow-2xl hover:shadow-purple-500/25 transition-all duration-300 hover:scale-105">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse"></div>
                <Zap className="h-5 w-5 text-purple-400" />
                Success Community
              </div>
            </div>
          </div>
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search documentation, tutorials, and guides..."
              className="w-full pl-10 pr-4 py-3 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
            />
          </div>
        </div>
      </section>

      {/* Support Channels */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Get Help When You Need It
            </h2>
            <p className="text-xl text-slate-300">
              Multiple ways to get support and find answers
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {supportChannels.map((channel, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 shadow-sm hover:shadow-lg transition-shadow duration-200">
                <div className="bg-primary-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                  <channel.icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{channel.channel}</h3>
                <p className="text-slate-300 text-sm mb-3">{channel.description}</p>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-green-600 font-medium">{channel.response}</span>
                  <span className="text-slate-400">{channel.best_for}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-slate-300">
              Quick answers to the most common questions
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
                  {expandedFaq === index ? (
                    <ChevronDown className="h-5 w-5 text-slate-400" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-slate-400" />
                  )}
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-slate-300">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
            Still Need Help?
          </h2>
          <p className="text-xl mb-8 text-slate-300">
            Our support team is here to help you succeed with your platform
          </p>
          
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 bg-opacity-10 rounded-lg p-6">
              <Mail className="h-8 w-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Email Support</h3>
              <p className="text-blue-100 text-sm mb-3">Get detailed help via email</p>
              <p className="text-blue-200 text-sm">Response: Within 24 hours</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 bg-opacity-10 rounded-lg p-6">
              <MessageCircle className="h-8 w-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Live Chat</h3>
              <p className="text-blue-100 text-sm mb-3">Quick questions and guidance</p>
              <p className="text-blue-200 text-sm">Available: 9am-6pm EST</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 bg-opacity-10 rounded-lg p-6">
              <Calendar className="h-8 w-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Schedule Call</h3>
              <p className="text-blue-100 text-sm mb-3">One-on-one consultation</p>
              <p className="text-blue-200 text-sm">Premium support only</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-yellow-400 text-primary-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-yellow-300 transition-colors duration-200">
              Contact Support
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-white/5 backdrop-blur-sm border border-white/10 hover:text-primary-600 transition-colors duration-200">
              Schedule Call
            </button>
          </div>
        </div>
      </section>

      {/* Resource Downloads */}
      <section className="py-16 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">
              Downloadable Resources
            </h2>
            <p className="text-slate-300">
              Helpful resources you can download and reference
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center shadow-sm">
              <FileText className="h-8 w-8 text-primary-600 mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Setup Checklist</h3>
              <p className="text-sm text-slate-300 mb-3">Complete platform setup checklist</p>
              <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                Download PDF
              </button>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center shadow-sm">
              <Code className="h-8 w-8 text-primary-600 mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Code Examples</h3>
              <p className="text-sm text-slate-300 mb-3">Common customization code snippets</p>
              <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                Download ZIP
              </button>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center shadow-sm">
              <Settings className="h-8 w-8 text-primary-600 mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Config Templates</h3>
              <p className="text-sm text-slate-300 mb-3">Pre-configured settings files</p>
              <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                Download
              </button>
            </div>
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6 text-center shadow-sm">
              <Users className="h-8 w-8 text-primary-600 mx-auto mb-3" />
              <h3 className="font-semibold text-white mb-2">Training Materials</h3>
              <p className="text-sm text-slate-300 mb-3">User training and onboarding materials</p>
              <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                Download
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact-form" className="py-20 relative z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-slate-300">
              Have questions? Want to get started? Fill out the form below and we'll get back to you within 24 hours.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 shadow-xl">
            {formSubmitted ? (
              <div className="text-center py-12">
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-6 mb-6">
                  <CheckCircle className="h-16 w-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-2">Message Sent Successfully!</h3>
                  <p className="text-slate-300">
                    Thank you for contacting us. We'll get back to you within 24 hours.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
                      placeholder="John Doe"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="package" className="block text-sm font-medium text-white mb-2">
                    Interested Package
                  </label>
                  <select
                    id="package"
                    name="package"
                    value={formData.package}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
                  >
                    <option value="not-selected" className="bg-slate-800">Not selected yet</option>
                    <option value="diy" className="bg-slate-800">DIY White-Label Package ($799)</option>
                    <option value="done-for-you" className="bg-slate-800">Done-For-You White-Label ($1,299)</option>
                    <option value="custom" className="bg-slate-800">Custom Enterprise Solution</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-white mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20"
                    placeholder="How can we help you?"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={6}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 resize-none"
                    placeholder="Tell us about your project, questions, or requirements..."
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    type="submit"
                    className="w-full sm:w-auto bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-lg text-lg font-bold hover:from-cyan-400 hover:to-blue-400 transition-all duration-200 shadow-lg hover:shadow-cyan-500/25 transform hover:scale-105"
                  >
                    Send Message
                  </button>
                  <div className="flex items-center gap-2 text-slate-300 text-sm">
                    <Headphones className="h-4 w-4" />
                    <span>Response within 24 hours</span>
                  </div>
                </div>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="bg-cyan-500/20 p-4 rounded-full w-fit mx-auto mb-4">
                <Mail className="h-6 w-6 text-cyan-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Email Us</h3>
              <p className="text-slate-300 text-sm">contact@yourdomain.com</p>
            </div>

            <div className="text-center">
              <div className="bg-blue-500/20 p-4 rounded-full w-fit mx-auto mb-4">
                <Calendar className="h-6 w-6 text-blue-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Schedule a Call</h3>
              <p className="text-slate-300 text-sm">Book a demo session</p>
            </div>

            <div className="text-center">
              <div className="bg-purple-500/20 p-4 rounded-full w-fit mx-auto mb-4">
                <MessageCircle className="h-6 w-6 text-purple-400" />
              </div>
              <h3 className="font-semibold text-white mb-2">Live Chat</h3>
              <p className="text-slate-300 text-sm">Available Mon-Fri 9AM-6PM</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportPage;
